/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useRef } from 'react';

import { css, jsx } from '@compiled/react';
import type { AIPlugin } from 'packages/editor/editor-plugin-ai/src/aiPluginType';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { RovoPromptScreenWithLogic } from '../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';
import { ModalRegionErrorBoundary } from '../../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { ModalRegion } from '../../ModalRegion/ModalRegion';
import { useUserDiscard } from '../hooks/useUserDiscard';
import type { Action } from '../useUIState';

// Ensures the div wrapping the prompt screen does not alter layout
const promptScreenCSS = css({ display: 'contents' });

function findModalDecoration(modalDecorationSet?: DecorationSet): Decoration | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}

	return (
		modalDecorationSet
			.find(
				undefined,
				undefined,
				(spec) =>
					spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
			)
			.shift() ||
		modalDecorationSet
			.find(undefined, undefined, (spec) => spec.key === 'ai-nodeDecoration')
			.shift()
	);
}

export function PromptSessionScreen({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}) {
	const { editorView, wrapperElement, isRovoLoading, endExperience } = useContentComponentContext();
	const { lastTriggeredFrom, modalMountedTimeStamp, modalDecorationSet, nextSessionId } =
		useSharedPluginStateWithSelector(api, ['aiExperience'], ({ aiExperienceState }) => ({
			lastTriggeredFrom: aiExperienceState?.aiDecorationExperiencePluginState.lastTriggeredFrom,
			modalMountedTimeStamp:
				aiExperienceState?.aiDecorationExperiencePluginState.modalMountedTimeStamp,
			modalDecorationSet: aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
			nextSessionId: aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
		}));

	const isDirectionalPinningEnabled = useMemo(() => {
		return !isRovoLoading;
	}, [isRovoLoading]);

	const promptScreenRef = useRef<HTMLDivElement>(null);

	const cancel = useCallback(
		(options?: { shouldClearSelectionContext?: boolean }) => {
			endExperience({ shouldClearSelectionContext: options?.shouldClearSelectionContext });
			api?.core?.actions.execute(resetSessionState());
			dispatch({ type: 'terminate' });
		},
		[endExperience, api?.core?.actions, dispatch],
	);

	useUserDiscard({
		ref: promptScreenRef,
		onDiscard: cancel,
	});

	// Get decoration for modal positioning
	const decoration = findModalDecoration(modalDecorationSet);

	if (!decoration || !modalDecorationSet) {
		return null;
	}

	// RovoInlineChatPrompt has its own internal logic with a loading screen
	// if a session is active, we'll show our own screen to prevent delay between
	// prompt submission and the rovo loading screen appearing
	if (nextSessionId) {
		return null;
	}

	return (
		<div ref={promptScreenRef} css={promptScreenCSS} data-testid="rovo-prompt-session-screen">
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				<ModalRegion
					isDirectionalPinningEnabled={isDirectionalPinningEnabled}
					lastTriggeredFrom={lastTriggeredFrom}
					key={modalMountedTimeStamp}
					decoration={decoration}
					editorView={editorView}
					editorRelativeWrapper={wrapperElement?.offsetParent}
					endExperience={endExperience}
					appearance={appearance}
					decorationSet={modalDecorationSet}
				>
					<RovoPromptScreenWithLogic cancel={cancel} />
				</ModalRegion>
			</ModalRegionErrorBoundary>
		</div>
	);
}
